import { ArrowRight, MessageCircle, ShieldCheck, TrendingUp, Activity, Cpu } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { CONTACT } from "@/lib/contacts";

const HEIGHTS = [38, 52, 30, 64, 46, 72, 40, 60, 50, 78, 58, 88, 68, 92, 80, 100];

const DashboardMock = () => (
  <figure className="relative w-full max-w-xl mx-auto" data-testid="hero-dashboard-mock">
    {/* Floating tags */}
    <div className="absolute -top-6 -left-3 z-10 rounded-2xl border border-[rgb(var(--brand-rgb)/0.4)] bg-[var(--bg)]/80 backdrop-blur-md px-3 py-2 flex items-center gap-2 float-y">
      <Activity className="h-4 w-4 text-[var(--brand)]" aria-hidden="true" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-200">Bot Online</div>
        <div className="text-xs font-bold text-white">12 / 12 active</div>
      </div>
    </div>
    <div
      className="absolute -bottom-6 -right-3 z-10 rounded-2xl border border-[rgb(var(--accent2-rgb)/0.4)] bg-[var(--bg)]/80 backdrop-blur-md px-3 py-2 flex items-center gap-2 float-y"
      style={{ animationDelay: "1.5s" }}
    >
      <Cpu className="h-4 w-4 text-[var(--accent2)]" aria-hidden="true" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-200">AI Engine</div>
        <div className="text-xs font-bold text-white">Optimized</div>
      </div>
    </div>

    <div className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-5 md:p-6 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(ellipse at top right, rgb(var(--brand-rgb) / 0.18), transparent 60%)",
        }}
      />
      <div className="relative flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-200">
            BTC/USDT · Grid+Average
          </div>
          <p className="font-heading text-2xl md:text-3xl font-black text-white tracking-tighter">
            $102,345.12
          </p>
        </div>
        <p className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-[rgb(var(--brand-rgb)/0.12)] text-[var(--brand)] text-xs font-bold">
          <TrendingUp className="h-3.5 w-3.5" aria-hidden="true" />
          +12.7%
        </p>
      </div>

      <div className="relative h-32 md:h-40 flex items-end gap-1.5 mt-2" aria-hidden="true">
        {HEIGHTS.map((h, i) => (
          <div
            key={i}
            className="mini-bar flex-1"
            style={{
              height: `${h}%`,
              opacity: 0.55 + (i / HEIGHTS.length) * 0.45,
            }}
          />
        ))}
      </div>

      <dl className="relative mt-5 grid grid-cols-3 gap-3">
        {[
          { k: "P/L Today", v: "+418.20", suffix: "USDT", positive: true },
          { k: "Open Orders", v: "47", suffix: "" },
          { k: "Win Rate", v: "99.9", suffix: "%" },
        ].map((m, i) => (
          <div
            key={i}
            className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5"
          >
            <dt className="text-[9px] uppercase tracking-[0.15em] text-zinc-300">
              {m.k}
            </dt>
            <dd
              className={`mt-0.5 ml-0 font-heading font-bold text-sm md:text-base ${
                m.positive ? "text-[var(--brand)]" : "text-white"
              }`}
            >
              {m.v}
              <span className="text-[10px] text-zinc-300 ml-1 font-normal">
                {m.suffix}
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </div>
    <figcaption className="sr-only">
      Ilustrasi dasbor AIOTrade: pasangan BTC/USDT dengan strategi Grid dan
      Average, profit harian +418,20 USDT, 47 order aktif, win rate 99,9%.
    </figcaption>
  </figure>
);

const Hero = () => {
  const { t } = useLanguage();

  return (
    <section
      id="top"
      aria-labelledby="hero-heading"
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24"
    >
      <div className="absolute inset-0 grid-bg pointer-events-none" aria-hidden="true" />
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 h-[460px] w-[860px] rounded-full pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(closest-side, rgb(var(--brand-rgb) / 0.22), rgb(var(--accent2-rgb) / 0.10) 45%, transparent 75%)",
          filter: "blur(60px)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        {/* Left content */}
        <div className="lg:col-span-7 text-center lg:text-left">
          <p className="flex justify-center lg:justify-start mb-6">
            <span
              data-testid="bappebti-badge"
              className="inline-flex items-center gap-2 px-3 md:px-4 h-8 md:h-9 rounded-full border border-[rgb(var(--brand-rgb)/0.3)] bg-[rgb(var(--brand-rgb)/0.05)] text-[10px] md:text-xs font-semibold tracking-[0.2em] text-[var(--brand)]"
            >
              <ShieldCheck className="h-3.5 w-3.5 md:h-4 md:w-4" aria-hidden="true" />
              {t.hero.badge}
            </span>
          </p>

          <h1
            id="hero-heading"
            className="font-heading font-black tracking-tighter leading-[0.95] text-white text-4xl sm:text-6xl lg:text-6xl xl:text-7xl"
          >
            {t.hero.title_1}{" "}
            <span className="text-[var(--brand)] glow-text">{t.hero.title_2}</span>
            <br />
            <span className="text-white/90">{t.hero.title_3}</span>
          </h1>

          <p className="mt-6 mx-auto lg:mx-0 max-w-xl text-base md:text-lg text-zinc-200 leading-relaxed">
            {t.hero.subtitle}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4">
            <a
              href={CONTACT.registration}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-register-btn"
              className="group inline-flex items-center justify-center gap-2 px-7 h-12 md:h-14 rounded-full bg-[var(--brand)] text-black font-bold text-sm md:text-base w-full sm:w-auto hover:bg-[var(--brand-hover)] transition-all hover:scale-[1.02] glow-ring"
            >
              {t.hero.cta_primary}
              <span className="sr-only"> {t.a11y.external}</span>
              <ArrowRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </a>
            <a
              href={CONTACT.whatsappAdmin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 px-7 h-12 md:h-14 rounded-full border-2 border-[#25D366] text-[#25D366] font-bold text-sm md:text-base w-full sm:w-auto hover:bg-[#25D366] hover:text-black transition-colors"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              {t.hero.cta_secondary}
              <span className="sr-only">
                {" "}
                {CONTACT.whatsappDisplay} {t.a11y.external}
              </span>
            </a>
          </div>

          {/* Hero stats */}
          <ul className="mt-10 md:mt-14 grid grid-cols-3 gap-3 md:gap-5 max-w-2xl mx-auto lg:mx-0">
            {[
              { value: "5K+", label: t.hero.stats_users },
              { value: "99.9%", label: t.hero.stats_success },
              { value: "24/7", label: t.hero.stats_uptime },
            ].map((stat, i) => (
              <li
                key={i}
                data-testid={`hero-stat-${i}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md px-3 md:px-5 py-4 md:py-5 text-center lg:text-left"
              >
                <span className="block font-heading text-2xl sm:text-3xl md:text-4xl font-black text-[var(--brand)] glow-text-soft tracking-tighter">
                  {stat.value}
                </span>
                <span className="mt-1 block text-[10px] md:text-xs uppercase tracking-[0.18em] text-zinc-200">
                  {stat.label}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5">
          <DashboardMock />
        </div>
      </div>
    </section>
  );
};

export default Hero;
